import { fetchAuthSession } from "aws-amplify/auth";

import type { Client, Config } from "../api/client";

import { getAppConfig } from "../config/get-app-config";
import { getValidAccessToken } from "./token";

const setAuthHeaders = async (req: Request): Promise<Request> => {
  const token = await getValidAccessToken();
  if (!token) return req;

  const headers = new Headers(req.headers);
  headers.set("Authorization", `Bearer ${token}`);

  // IMPORTANT: Explicitly remove x-mvz-auth even though the swagger mentions it; sending it causes CORS issues.
  // The generated client or upstream code may add this header via security config, so we delete it here.
  headers.delete("x-mvz-auth");

  return new Request(req, { headers });
};

const retryingFetch: typeof fetch = async (input, init): Promise<Response> => {
  // This will retry the request if it fails with a 401
  const originalReq =
    input instanceof Request ? input : new Request(input, init);

  // Important: clone up-front so we can reuse body on retry
  const attempt1Base = originalReq.clone();
  const attempt2Base = originalReq.clone();

  const send = async (baseReq: Request): Promise<Response> => {
    const authedReq = await setAuthHeaders(baseReq);
    return fetch(authedReq);
  };

  const res1 = await send(attempt1Base);
  if (res1.status !== 401) return res1;

  // Refresh once, then retry once
  await fetchAuthSession({ forceRefresh: true });
  return send(attempt2Base);
};

/**
 * Patch the default API client to use token refresh and 401 retry.
 * Call this once at app startup so all SDK calls (direct and via stores) use it.
 */
export const initAuthedApi = (apiClient: Pick<Client, "setConfig">): void => {
  const config: Pick<Config, "baseUrl" | "fetch"> = {
    baseUrl: getAppConfig().backendUrl,
    fetch: retryingFetch,
  };
  apiClient.setConfig(config);
};
